'use strict';

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the Authentication service.
 * Uses Q-based promises for all operations.
 */
/* global jQuery */
(function ($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Creates a new AuthenticationService object.
   *
   * Argument 'authenticationServiceUrl' - The URL to the authentication service in the Content REST API.
   * Argument 'serviceSettings' - An object containing settings for this service.
   *
   * The 'serviceSettings' object should look like the following:
   *
   * {
   *   customQ: customQObject
   * }
   *
   * Property 'customQ' is an implementation of a promise based Q library,
   * i.e Angular $q. Defaults to a global Q.
   *
   * Returns a new AuthenticationService object.
   */
  atex.aceui.service.AuthenticationService = function (authenticationServiceUrl, serviceSettings) {

    var settings = extend({}, serviceSettings);
    var q = settings.customQ || window.Q;

    return {
      /**
       * Verifies whether the given authentication token is still valid.
       *
       * Argument 'authToken' - The authentication token to verify.
       *
       * Returns a Q-based promise.
       *
       * If the operation succeeds the returned promise is resolved with an object like:
       *
       * {
       *   userId: 'USERID',
       *   token: 'TOKEN'
       * }
       *
       * Property 'userId' is the user ID of the user contained in the token.
       * Property 'token' is the input token.
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
      validate: function (authToken) {
        var deferred = q.defer();

        $.ajax(authenticationServiceUrl, {
          type: 'GET',
          contentType: 'application/json',
          dataType: 'json',
          cache: false,

          beforeSend: function (jqXHR) {
            jqXHR.setRequestHeader('X-Auth-Token', authToken);
          },

          success: function (data) {
            deferred.resolve(data);
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.AuthenticationService', {
              401: 'User is not correctly authenticated!',
              'default': 'Request failed while checking authentication status!'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Authenticates using the given username and password.
       *
       * Argument 'username' - The username to authenticate with.
       * Argument 'password' - The password to authenticate with.
       *
       * Returns a Q-based promise.
       *
       * If the operation succeeds the returned promise is resolved with an object like:
       *
       * {
       *   userId: 'USERID',
       *   token: 'TOKEN'
       * }
       *
       * Property 'userId' is the user ID of the user that was just authenticated.
       * Property 'token' is the resulting authentication token.
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
      authenticate: function ({ username, password, exchange }) {
        var deferred = q.defer();

        let url = authenticationServiceUrl;

        if (exchange) {
          username = undefined;
          password = undefined;
        }

        $.ajax(url, {
          type: 'POST',
          dataType: 'json',
          contentType: 'application/json',
          data: JSON.stringify ({ username, password, token: exchange }),

          success: function (data) {
            deferred.resolve(data);
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.AuthenticationService', {
              400: 'Something was wrong with the request when authenticating',
              401: 'Failed to authenticate user!',
              'default': 'Server error while authenticating user!'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Invalidates the given authentication token.
       *
       * Argument 'authToken' - The authentication token to invalidate.
       *
       * Returns a Q-based promise.
       *
       * If the operation succeeds the returned promise is resolved without any value.
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
      invalidate: function (authToken) {
        var deferred = q.defer();

        $.ajax(authenticationServiceUrl, {
          type: 'DELETE',
          dataType: 'json',

          beforeSend: function (jqXHR, settings) {
            jqXHR.setRequestHeader('X-Auth-Token', authToken);
          },

          success: function (data, textStatus, jqXHR) {
            deferred.resolve();
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.AuthenticationService', {
              'default': 'Server error while destroying authentication token!'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Get resource token
       */
      getResourceToken: function (username, token, resource) {
        const deferred = q.defer();

        const payload = {
          username: username,
          token: token
        };

        const previewTokenUrl = authenticationServiceUrl + '?resource=' + resource;

        $.ajax(previewTokenUrl, {
          type: 'POST',
          dataType: 'json',
          contentType: 'application/json',
          data: JSON.stringify(payload),

          success: function (data, textStatus, jqXHR) {
            deferred.resolve(data);
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.AuthenticationService', {
              'default': 'Server error while getting preview token!'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      }
    };
  };
})(jQuery);
